"use client";

import clsx from "clsx";
import { kebabCase } from "lodash";
import { useFilterNavigation } from "@repo/next-utils/use-filter-navigation";
import * as Accordion from "@repo/ui/accordion";
import { Box } from "@repo/ui/box";
import { Button } from "@repo/ui/button";
import { CheckboxButton } from "@repo/ui/checkbox-button";
import * as Flex from "@repo/ui/flex";
import * as Grid from "@repo/ui/grid";
import { Label } from "@repo/ui/label";
import { NumberInput } from "@repo/ui/number-input";
import {
  FILTERING_ACTIONS,
  useProductFiltering,
} from "@repo/ui/product-filtering";
import * as RadioGroup from "@repo/ui/radio-group";
import * as SheetComponent from "@repo/ui/sheet";
import { Text } from "@repo/ui/text";
import { VisuallyHidden } from "@repo/ui/visually-hidden";
import { STRINGS } from "@/constants";
import { ChevronIcon } from "@/theme/icons/chevron";
import { ChevronLeft } from "@/theme/icons/chevron-left";
import { ChevronRight } from "@/theme/icons/chevron-right";
import { CloseIcon } from "@/theme/icons/close";
import {
  TYPOGRAPHY_LABEL3,
  TYPOGRAPHY_HEADING4,
  TYPOGRAPHY_LINK2_CAPS,
  TYPOGRAPHY_BODY3_SENTENCE,
} from "@/theme/topshop/typography/typography-constants";

import {
  EnrichedFacet,
  EnrichedFacetValue,
} from "@/utils/facets/enrich-facets";
import { getFacetLayout } from "@/utils/facets/get-facet-layout";
import { useSmallViewport } from "../viewport";
import classes from "./filters.module.css";

type BaseProps = {
  children: React.ReactNode;
  title: string;
};

export const Base = ({ children, title }: BaseProps) => {
  const isMobile = useSmallViewport();
  const { state, dispatch } = useProductFiltering();
  const handleFilterNavigation = useFilterNavigation();

  const isFiltersEmpty = Object.entries(state.filters).every(
    ([, value]) =>
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === "string" && value === ""),
  );

  const toggleFilterModal = () => {
    dispatch({
      type: FILTERING_ACTIONS.SET_IS_OPEN,
      isOpen: !state.isOpen,
    });
  };

  const handleClearAll = () => {
    dispatch({
      type: FILTERING_ACTIONS.CLEAR_ALL_FILTERS,
    });
  };

  const handleApply = () => {
    handleFilterNavigation(state.filters);
    toggleFilterModal();
  };

  const totalFilterCount = Object.values(state.filters).reduce(
    (acc, value) => acc + (Array.isArray(value) ? value.length : 0),
    0,
  );

  return (
    <SheetComponent.Root open={state.isOpen} onOpenChange={toggleFilterModal}>
      <SheetComponent.Trigger
        className={clsx(TYPOGRAPHY_LABEL3, classes.filtersTrigger)}
      >
        {title}
        <ChevronIcon className={classes.chevron} />
      </SheetComponent.Trigger>
      <Flex.Container justify="space-between" column asChild>
        <SheetComponent.Content
          position={isMobile ? "bottom" : "right"}
          overlayAnimation
          className={classes.sheet}
        >
          <Flex.Container
            column
            justify="space-between"
            gap="sm"
            className={classes.contentContainer}
          >
            <Flex.Container justify="space-between" asChild align="center">
              <Box paddingY="xs" asChild>
                <SheetComponent.Header className={classes.header}>
                  <SheetComponent.Title asChild>
                    <h2
                      className={clsx(
                        TYPOGRAPHY_HEADING4,
                        !isMobile && "visuallyHidden",
                      )}
                    >
                      {title}
                    </h2>
                  </SheetComponent.Title>
                  {!isFiltersEmpty && isMobile && (
                    <Button
                      className={clsx(
                        TYPOGRAPHY_LABEL3,
                        classes.clearAll,
                        classes.underline,
                      )}
                      alignment="center"
                      onClick={handleClearAll}
                    >
                      Clear All Filters
                    </Button>
                  )}
                  <VisuallyHidden>
                    <SheetComponent.Description>
                      Apply filters to your results
                    </SheetComponent.Description>
                  </VisuallyHidden>
                  <SheetComponent.Close className={classes.closeBtn}>
                    <CloseIcon className={classes.closeIcon} />
                  </SheetComponent.Close>
                </SheetComponent.Header>
              </Box>
            </Flex.Container>
            <SheetComponent.Main className={classes.main}>
              <Flex.Container
                justify="space-between"
                column
                className={classes.flexGroup}
              >
                {children}
              </Flex.Container>
            </SheetComponent.Main>
            <SheetComponent.Footer className={classes.footer}>
              {!isFiltersEmpty && !isMobile && (
                <Button
                  className={clsx(
                    TYPOGRAPHY_LABEL3,
                    classes.clearAll,
                    classes.underline,
                  )}
                  alignment="center"
                  onClick={handleClearAll}
                >
                  Clear All Filters
                </Button>
              )}
              <Button
                alignment="start"
                variant={isMobile ? "primary" : "secondary"}
                size="medium"
                fullWidth
                onClick={handleApply}
                className={TYPOGRAPHY_LABEL3}
              >
                Apply{totalFilterCount > 0 ? ` (${totalFilterCount})` : ""}
              </Button>
            </SheetComponent.Footer>
          </Flex.Container>
        </SheetComponent.Content>
      </Flex.Container>
    </SheetComponent.Root>
  );
};

export const FacetsAccordion = ({ facets }: { facets: EnrichedFacet[] }) => {
  const { dispatch, state } = useProductFiltering();
  const handleSelectAll = (facet: EnrichedFacet) => {
    dispatch({
      type: FILTERING_ACTIONS.UPDATE_FILTER,
      filter: facet.id,
      values: facet.facetValues.map((value) => value.id),
    });
  };

  const handleClearAll = (facet: EnrichedFacet) => {
    dispatch({
      type: FILTERING_ACTIONS.CLEAR_FILTER,
      filter: facet.id,
    });
  };

  return (
    <Accordion.Root variant="dark">
      {facets.map((facet: EnrichedFacet) => {
        const layout = getFacetLayout(facet);
        const selectedCount =
          layout?.component === "checkbox"
            ? state.filters[facet.id]?.length || 0
            : 0;

        return (
          <Accordion.Item
            key={facet.name}
            value={facet.name}
            className={classes.accordionItem}
          >
            <Accordion.Header className={classes.accordionHeader}>
              <Accordion.Trigger
                className={`${classes.accordionTrigger} ${TYPOGRAPHY_LABEL3}`}
              >
                <span>
                  {facet.name}
                  {selectedCount > 0 && ` (${selectedCount})`}
                </span>
                <Accordion.TriggerIcon />
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className={classes.accordionContent}>
              <Flex.Container column gap="xs">
                <FacetLayout facet={facet} />
                {layout?.component === "checkbox" && (
                  <Flex.Container
                    justify="space-between"
                    className={classes.selectAll}
                  >
                    <Button
                      className={clsx(
                        TYPOGRAPHY_LABEL3,
                        classes.selectAllButton,
                      )}
                      size="small"
                      onClick={() => handleSelectAll(facet)}
                    >
                      SELECT ALL
                    </Button>
                    <Button
                      className={clsx(
                        TYPOGRAPHY_LABEL3,
                        classes.selectAllButton,
                      )}
                      size="small"
                      onClick={() => handleClearAll(facet)}
                    >
                      CLEAR ALL
                    </Button>
                  </Flex.Container>
                )}
              </Flex.Container>
            </Accordion.Content>
          </Accordion.Item>
        );
      })}
    </Accordion.Root>
  );
};

export const FacetsSheets = ({ facets }: { facets: EnrichedFacet[] }) => {
  return (
    <>
      {facets.map((facet: EnrichedFacet) => (
        <Facet key={facet.id} facet={facet} />
      ))}
    </>
  );
};

export const FacetLayout = ({ facet }: { facet: EnrichedFacet }) => {
  const layout = getFacetLayout(facet);
  if (!layout) return null;

  const facetLayoutMap = {
    radio: RadioView,
    checkbox: CheckboxView,
    range: PricingView,
  } as const;

  const LayoutComponent = facetLayoutMap[layout.component];
  return (
    <LayoutComponent
      facet={facet}
      {...(layout.component === "checkbox" &&
        layout.columns && { columns: layout.columns })}
    />
  );
};

export const SelectClearAllButton = ({ facet }: { facet: EnrichedFacet }) => {
  const { state, dispatch } = useProductFiltering();

  const currentFilter = state?.filters?.[facet.id];
  const currentFilterExists = Boolean(currentFilter?.length);

  const handleClick = () => {
    if (currentFilterExists) {
      dispatch({
        type: FILTERING_ACTIONS.CLEAR_FILTER,
        filter: facet.id,
      });
    } else {
      dispatch({
        type: FILTERING_ACTIONS.UPDATE_FILTER,
        filter: facet.id,
        values: facet.facetValues.map((value) => value.id),
      });
    }
  };

  return (
    <Button
      className={TYPOGRAPHY_LINK2_CAPS}
      alignment="center"
      onClick={handleClick}
    >
      {!currentFilterExists ? "Select All" : "Clear All"}
    </Button>
  );
};

export const Facet = ({ facet }: { facet: EnrichedFacet }) => {
  const layout = getFacetLayout(facet);
  const { state, dispatch } = useProductFiltering();
  const handleFilterNavigation = useFilterNavigation();

  const selectedCount =
    layout?.component === "checkbox" ? state.filters[facet.id]?.length || 0 : 0;

  const handleApply = () => {
    handleFilterNavigation(state.filters);
    dispatch({
      type: FILTERING_ACTIONS.SET_IS_OPEN,
      isOpen: !state.isOpen,
    });
  };

  return (
    <SheetComponent.Root>
      <SheetComponent.Trigger className={classes.trigger}>
        <Flex.Container
          align="center"
          justify="space-between"
          className={classes.group}
        >
          <Text className={TYPOGRAPHY_LABEL3}>
            {facet.name}
            {selectedCount > 0 && ` (${selectedCount})`}
          </Text>
          <ChevronRight className={classes.icon} />
        </Flex.Container>
      </SheetComponent.Trigger>
      <SheetComponent.Content
        position="right"
        overlayAnimation
        className={clsx(classes.sheet, classes.facetSheet)}
        showOverlay={false}
      >
        <Flex.Container
          column
          justify="space-between"
          gap="sm"
          className={classes.contentContainer}
        >
          <Flex.Container justify="space-between" asChild align="center">
            <SheetComponent.Header>
              <Flex.Container align="center" gap="2xs">
                <Flex.Container align="center" asChild>
                  <Button size="icon" asChild className={classes.leftChevron}>
                    <SheetComponent.Close aria-label="Back to filters">
                      <ChevronLeft width={6} height={10} />
                    </SheetComponent.Close>
                  </Button>
                </Flex.Container>
                <SheetComponent.Title className={TYPOGRAPHY_HEADING4}>
                  {facet.name}
                </SheetComponent.Title>
              </Flex.Container>
              {layout?.component === "checkbox" ? (
                <SelectClearAllButton facet={facet} />
              ) : null}
              <VisuallyHidden>
                <SheetComponent.Description>
                  {STRINGS.filters.ariaLabels.applyFiltersDescription}
                </SheetComponent.Description>
              </VisuallyHidden>
            </SheetComponent.Header>
          </Flex.Container>
          <SheetComponent.Main className={classes.main}>
            <FacetLayout facet={facet} />
          </SheetComponent.Main>
          <SheetComponent.Footer>
            <SheetComponent.Close asChild>
              <Button
                className={TYPOGRAPHY_LABEL3}
                alignment="start"
                variant="primary"
                size="medium"
                fullWidth
                onClick={handleApply}
              >
                {STRINGS.filters.apply}
              </Button>
            </SheetComponent.Close>
          </SheetComponent.Footer>
        </Flex.Container>
      </SheetComponent.Content>
    </SheetComponent.Root>
  );
};

export const RadioView = ({ facet }: { facet: EnrichedFacet }) => {
  const { state, dispatch } = useProductFiltering();

  const handleValueChange = (value: string) => {
    dispatch({
      type: FILTERING_ACTIONS.CHANGE_FILTER,
      filter: facet.id,
      value,
    });
  };

  return (
    <RadioGroup.Root
      value={(state.filters[facet.id] as string) || ""}
      onValueChange={handleValueChange}
    >
      {facet.facetValues.map((item: EnrichedFacetValue) => {
        const { id, name } = item;
        const radioId = `${facet.id}-${kebabCase(name)}`;
        return (
          <Box key={id} asChild borderColor="secondary">
            <Flex.Container align="center">
              <RadioGroup.Item
                value={id}
                id={radioId}
                className={classes.radioItem}
              />
              <Box paddingX="sm" paddingY="xs" asChild>
                <Flex.Item grow={1} asChild>
                  <Label htmlFor={radioId} className={TYPOGRAPHY_LABEL3}>
                    {name}
                  </Label>
                </Flex.Item>
              </Box>
            </Flex.Container>
          </Box>
        );
      })}
    </RadioGroup.Root>
  );
};

type CheckboxViewProps = {
  facet: EnrichedFacet;
  columns?: number;
};

export const CheckboxView = ({ facet, columns }: CheckboxViewProps) => {
  const { state, dispatch } = useProductFiltering();
  const isChecked = (value: string) =>
    state.filters[facet.id]?.includes(value) ?? false;

  const handleChange = (value: string) => {
    dispatch({
      type: isChecked(value)
        ? FILTERING_ACTIONS.REMOVE_FILTER
        : FILTERING_ACTIONS.ADD_FILTER,
      filter: facet.id,
      value,
    });
  };

  return (
    <>
      {columns ? (
        <Grid.Container columns={columns} gap="xs">
          {facet.facetValues.map((item: EnrichedFacetValue) => (
            <CheckboxButton
              name={item.name}
              checked={isChecked(item.id)}
              onCheckedChange={() => handleChange(item.id)}
              className={classes.checkboxButton}
              key={item.id}
            >
              <Label className={TYPOGRAPHY_LABEL3}>{item.name}</Label>
            </CheckboxButton>
          ))}
        </Grid.Container>
      ) : (
        <Flex.Container gap="xs" wrap className={classes.checkboxContainer}>
          {facet.facetValues.map((item: EnrichedFacetValue) => (
            <Flex.Container align="center" key={item.id} asChild>
              <CheckboxButton
                name={item.name}
                checked={isChecked(item.id)}
                onCheckedChange={() => handleChange(item.id)}
                className={classes.checkboxButton}
              >
                {facet.name === "Colour" ? (
                  <Flex.Container gap="xs" align="center">
                    <div
                      className={classes.colourBox}
                      style={{
                        background:
                          item.name !== "Multi"
                            ? item.enrichment?.hexColour
                            : "conic-gradient(from 180deg at 50% 50%, #F82407 0deg, #E9EE17 118.80000472068787deg, #1997F2 241.2000060081482deg, #F01FB5 341.9999957084656deg, #F82407 360deg)",
                      }}
                    />
                    <Label className={clsx(TYPOGRAPHY_LABEL3)}>
                      {item.name}
                    </Label>
                  </Flex.Container>
                ) : (
                  <Label className={clsx(TYPOGRAPHY_LABEL3)}>{item.name}</Label>
                )}
              </CheckboxButton>
            </Flex.Container>
          ))}
        </Flex.Container>
      )}
    </>
  );
};

export const PricingView = ({ facet }: { facet: EnrichedFacet }) => {
  const { state, dispatch } = useProductFiltering();

  const priceMin = Math.floor(Number(facet.facetValues[0]?.name));
  const priceMax = Math.floor(Number(facet.facetValues[1]?.name));

  const handleChange = (value: string, type: "priceMin" | "priceMax") => {
    dispatch({
      type: FILTERING_ACTIONS.CHANGE_FILTER,
      filter: type,
      value,
    });

    const otherType = type === "priceMin" ? "priceMax" : "priceMin";
    const otherValue = state.filters[otherType];
    if (!otherValue) {
      dispatch({
        type: FILTERING_ACTIONS.CHANGE_FILTER,
        filter: otherType,
        value: type === "priceMin" ? priceMax.toString() : priceMin.toString(),
      });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    let value = e.target.value;
    const inputName = e.target.name as "priceMin" | "priceMax";

    if (value !== "" && parseFloat(value) < priceMin) {
      value = priceMin.toString();
    } else if (value !== "" && parseFloat(value) > priceMax) {
      value = priceMax.toString();
    }

    dispatch({
      type: FILTERING_ACTIONS.CHANGE_FILTER,
      filter: inputName,
      value,
    });

    const otherType = inputName === "priceMin" ? "priceMax" : "priceMin";
    const otherValue = state.filters[otherType];
    if (!otherValue) {
      dispatch({
        type: FILTERING_ACTIONS.CHANGE_FILTER,
        filter: otherType,
        value:
          inputName === "priceMin" ? priceMax.toString() : priceMin.toString(),
      });
    }
  };

  return (
    <Flex.Container align="end" gap="sm">
      <Flex.Container
        column
        gap="2xs"
        className={classes.pricingInputContainer}
      >
        <Label className={TYPOGRAPHY_LABEL3} htmlFor="priceMin">
          min (£)
        </Label>
        <NumberInput
          className={classes.pricingInput}
          placeholder={priceMin.toString()}
          min={priceMin}
          max={priceMax}
          onBlurCapture={handleBlur}
          onChange={(value: string) => handleChange(value, "priceMin")}
          id="priceMin"
          name="priceMin"
          wholeNumbers={true}
          value={state.filters.priceMin}
        />
      </Flex.Container>
      <Box paddingY="sm">
        <span className={TYPOGRAPHY_BODY3_SENTENCE}>to</span>
      </Box>
      <Flex.Container
        column
        gap="2xs"
        className={classes.pricingInputContainer}
      >
        <Label className={TYPOGRAPHY_LABEL3} htmlFor="priceMax">
          max (£)
        </Label>
        <NumberInput
          className={classes.pricingInput}
          placeholder={priceMax.toString()}
          min={priceMin}
          max={priceMax}
          onBlurCapture={handleBlur}
          onChange={(value: string) => handleChange(value, "priceMax")}
          id="priceMax"
          name="priceMax"
          wholeNumbers={true}
          value={state.filters.priceMax}
        />
      </Flex.Container>
    </Flex.Container>
  );
};

export const Filters = ({
  facets,
  title,
}: {
  facets: EnrichedFacet[];
  title: string;
}) => {
  const isMobile = useSmallViewport();

  return (
    <Base title={title}>
      {isMobile ? (
        <FacetsSheets facets={facets} />
      ) : (
        <FacetsAccordion facets={facets} />
      )}
    </Base>
  );
};
